import {
  IPostReqSecurityAuthenticationLogin,
  IPostReqSecurityResetPasswordRequest,
  IPostReqSecurityResetPasswordReset,
  IProfile,
  IProfileBuilding,
  IPutReqUserProfile,
} from '../../frontend-libs/evlapp-types';
import { createAsyncThunk as asyncThunk, createSlice } from '@reduxjs/toolkit';

import { userLogin } from '../../Login/services/loginService';
import { changePassword, resetPassword } from '../../PasswordReset/services/PasswordResetService';
import { getUserProfile, updateUserProfile, updateUserProfileRequest } from '../../Profile/services/ProfileService';
import { registerAdmin as register, registerBuilding } from '../../Registration/services/RegistrationService';
import { localStorage, RestApi } from '../services';
import type { RootState } from './store';

import { IAdminRegistration } from '../../Registration/Admin/interfaces';
import { registerMemberWithInvitation } from '../../Registration/Invitation/services/InvitationService';

interface SecurityState {
  state: 'authenticated' | 'notAuthenticated' | 'pending' | 'badCredentials';
  token?: string | null;
  profile?: IProfile;
  buildings?: IProfileBuilding[];
  ui: {
    state: 'member' | 'admin';
    buildingId: string | null;
    buildingState: 'initialization' | 'loading' | 'ready' | 'error';
    address: string | null;
    isAdmin: boolean;
    isMember: boolean;
    subscription: {
      isExpired: boolean;
      expiresAt: string;
      inGracePeriod: boolean;
      gracePeriodEndsAt: string;
      isAfterGracePeriod: boolean;
    };

    tokens: IProfileBuilding['tokens'];
    licenseValidTo?: IProfileBuilding['licenseValidTo'];
    openOrder?: IProfileBuilding['openOrder'];
    autoRenew?: IProfileBuilding['autoRenew'];
  };
}

const initialState: SecurityState = {
  state: 'notAuthenticated',
  token: localStorage.getToken(),
  ui: {
    state: 'member',
    buildingId: null,
    address: null,
    isAdmin: false,
    isMember: false,
    buildingState: 'error',
    tokens: [],
    subscription: {
      isExpired: false,
      expiresAt: '',
      inGracePeriod: false,
      gracePeriodEndsAt: '',
      isAfterGracePeriod: false,
    },
  },
};

export const loginUser = asyncThunk(
  'security/login',
  async (values: IPostReqSecurityAuthenticationLogin, { rejectWithValue }) => {
    try {
      return await userLogin(values);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getProfile = asyncThunk('security/profile/get', async () => {
  return getUserProfile();
});

/**
 * Tiché obnovení profilu/budov (tokeny, licenseValidTo, openOrder) BEZ přepnutí do stavu `pending`.
 * `getProfile` přepíná `state = 'pending'` → PrivateRoute ukáže celostránkový spinner a celá stránka se
 * odpojí a znovu připojí (reset formulářů, u stránky stavu objednávky i smyčka). Selhání se ignoruje.
 */
export const refreshProfile = asyncThunk('security/profile/refresh', async () => {
  return getUserProfile();
});

export const updateProfile = asyncThunk(
  'security/profile/update',
  async (values: IPutReqUserProfile, { rejectWithValue }) => {
    try {
      return await updateUserProfile(values);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateProfileProtectedRequest = asyncThunk(
  'security/profile/update-request',
  async (values: IPutReqUserProfile, { rejectWithValue }) => {
    try {
      return await updateUserProfileRequest(values);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const registerUserWithInvitation = asyncThunk(
  'security/register/user-invitation',
  async (
    values: { token: string; email: string; password: string; agreements?: Record<string, boolean> },
    { rejectWithValue }
  ) => {
    try {
      return await registerMemberWithInvitation(values);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const registerAdmin = asyncThunk(
  'security/register/admin',
  async (values: IAdminRegistration, { rejectWithValue }) => {
    try {
      return await register(values);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const registerBuildingForExistingUser = asyncThunk(
  'security/register/building',
  async (values: IAdminRegistration & { promoCode?: { code?: string } }, { rejectWithValue }) => {
    try {
      return await registerBuilding(values);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const passwordReset = asyncThunk(
  'security/password-reset/request',
  async (values: IPostReqSecurityResetPasswordRequest) => {
    return resetPassword(values);
  }
);

export const passwordChange = asyncThunk(
  'security/password-reset/change',
  async (values: IPostReqSecurityResetPasswordReset) => {
    return changePassword(values);
  }
);

// Preferuje posledni zvolenou budovu z localStorage, aby volba prezila reload / novy login
const pickDefaultBuilding = (buildings?: IProfileBuilding[]): IProfileBuilding | null => {
  if (!buildings || buildings.length === 0) return null;

  const lastBuildingId = localStorage.getLastBuildingId();
  return buildings.find((building) => building.buildingId === lastBuildingId) || buildings[0];
};

const setUIState = (state: SecurityState['ui'], payload: IProfileBuilding | null): SecurityState['ui'] => {
  if (!payload) return state;
  const { profileId: _profileId, ...rest } = payload;
  let userState: SecurityState['ui']['state'] = 'member';
  if (!payload.isMember && payload.isAdmin) userState = 'admin';

  let subscription = {
    isExpired: false,
    inGracePeriod: false,
    isAfterGracePeriod: false,
    expiresAt: '',
    gracePeriodEndsAt: '',
  };

  if (payload.subscription) {
    subscription = {
      isExpired: payload.subscription.isExpired || false,
      inGracePeriod: payload.subscription.inGracePeriod || false,
      isAfterGracePeriod: (payload.subscription.isExpired && !payload.subscription.inGracePeriod) || false,
      expiresAt: payload?.subscription?.expiresAt || '',
      gracePeriodEndsAt: payload.subscription.gracePeriodEndsAt || '',
    };
  }

  return {
    ...state,
    ...rest,
    state: userState,
    buildingState: payload.state || 'ready',
    subscription: subscription,
  };
};

export const securitySlice = createSlice({
  name: 'security',
  initialState,
  reducers: {
    toggleUI: (state) => {
      let newState: SecurityState['ui']['state'] = 'member';
      if (state.ui.state === 'member') newState = 'admin';

      state.ui = { ...state.ui, state: newState };
    },
    changeBuilding: (state, { payload }) => {
      let newState: SecurityState['ui']['state'] = 'member';
      if (!payload.isMember && payload.isAdmin) newState = 'admin';

      let subscription = {
        isExpired: false,
        inGracePeriod: false,
        isAfterGracePeriod: false,
        expiresAt: '',
        gracePeriodEndsAt: '',
      };

      if (payload.subscription) {
        subscription = {
          isExpired: payload.subscription.isExpired || false,
          inGracePeriod: payload.subscription.inGracePeriod || false,
          isAfterGracePeriod: (payload.subscription.isExpired && !payload.subscription.inGracePeriod) || false,
          expiresAt: payload?.subscription?.expiresAt || '',
          gracePeriodEndsAt: payload.subscription.gracePeriodEndsAt || '',
        };
      }

      state.ui = {
        ...state.ui,
        ...payload,
        state: newState,
        buildingState: payload.state || 'ready',
        subscription: subscription,
      };

      if (payload.buildingId) localStorage.setLastBuildingId(payload.buildingId);
    },
    logout: () => {
      localStorage.deleteToken();
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.state = 'pending';
    });

    builder.addCase(loginUser.fulfilled, (state, { payload }) => {
      state.state = 'authenticated';
      state.token = payload?.token;
      state.profile = payload.profile;
      state.buildings = payload.buildings;

      const defaultBuilding = pickDefaultBuilding(state.buildings);

      setUIState(state.ui, defaultBuilding);
      state.ui = setUIState(state.ui, defaultBuilding);

      if (payload.token) {
        RestApi.setToken(payload.token);
        localStorage.setToken(payload.token);
      }
    });

    builder.addCase(registerUserWithInvitation.fulfilled, (state, { payload }) => {
      state.state = 'authenticated';
      state.token = payload?.token;
      state.profile = payload.profile;
      state.buildings = payload.buildings;

      const defaultBuilding = state.buildings?.[0] || null;

      setUIState(state.ui, defaultBuilding);
      state.ui = setUIState(state.ui, defaultBuilding);

      if (payload.token) {
        RestApi.setToken(payload.token);
        localStorage.setToken(payload.token);
      }
    });

    builder.addCase(getProfile.pending, (state) => {
      state.state = 'pending';
    });

    builder.addCase(getProfile.rejected, (state) => {
      state.state = 'badCredentials';
      RestApi.removeToken();
      localStorage.deleteToken();
    });

    builder.addCase(getProfile.fulfilled, (state, { payload }) => {
      state.state = 'authenticated';
      state.profile = payload.profile;
      state.buildings = payload.buildings;

      const defaultBuilding = pickDefaultBuilding(state.buildings);
      state.ui = setUIState(state.ui, defaultBuilding);
    });

    builder.addCase(refreshProfile.fulfilled, (state, { payload }) => {
      state.profile = payload.profile;
      state.buildings = payload.buildings;
      state.ui = setUIState(state.ui, pickDefaultBuilding(state.buildings));
    });

    builder.addCase(updateProfile.fulfilled, (state, { payload }) => {
      state.profile = payload.profile;
    });

    builder.addCase(registerBuildingForExistingUser.fulfilled, (state, { payload }) => {
      // Token i aktivni budova zustavaji - na novou budovu prepne komponenta explicitne pres changeBuilding
      state.profile = payload.profile;
      state.buildings = payload.buildings;
    });

    builder.addCase(registerAdmin.fulfilled, (state, { payload }) => {
      state.state = 'authenticated';
      state.token = payload?.token;
      state.profile = payload.profile;
      state.buildings = payload.buildings;

      const defaultBuilding = state.buildings?.[0] || null;
      state.ui = setUIState(state.ui, defaultBuilding);

      if (payload.token) {
        RestApi.setToken(payload.token);
        localStorage.setToken(payload.token);
      }
    });
  },
});

export const { toggleUI, logout, changeBuilding } = securitySlice.actions;
export const selectSecurity = (state: RootState): SecurityState => state.security;
export default securitySlice.reducer;
